import {
  Typography,
  Button,
  IconButton,
  Box,
  Select,
  MenuItem,
  styled,
  Skeleton,
  Menu,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import {
  PhoneIcon,
  DotsThreeVerticalIcon,
  CopyIcon,
  TrashSimpleIcon,
  CheckIcon,
  PencilSimpleIcon,
  NotebookIcon,
  CaretDownIcon,
  ForkKnifeIcon,
  ArrowLeftIcon,
  WhatsappLogoIcon,
  LinkBreakIcon,
} from '@phosphor-icons/react';
import theme from '../../theme';
import PhoneModal from '../modals/PhoneModal';
import { SelectChangeEvent } from '@mui/material/Select';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import { useDashboard } from '../../hooks/useDashboard';
import { useAgents } from '../../hooks/useAgents';
import DeleteModal from '../modals/DeleteModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteAgentApi,
  updateAgentApi,
  saveAgentKnowledgeBaseApi,
} from '../../utils/apis';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';
import { IUpdateAssistant } from '../../schemas/apis/interface';
import { useUser } from '../../hooks/useUser';
import { routes } from '../../constants/routes';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { startCall } from '../../utils/redux/slices/CallSlice';
import { useStores } from '../../components/UserJourny/FirstUserJourney/useStores';
import { RootState } from '../../utils/redux';
import { useTemplates } from '../../hooks/useTemplates';
import MenuDrawer from './MenuDrawer';
import ConnectWhatsappAgentModal from '../modals/ConnectWhatsappAgentModal';

export const HeaderContainer = styled(Box)({
  position: 'sticky',
  top: 0,
  display: 'flex',
  padding: '16px 24px',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '8px',
  border: '1px solid #DCE0E7',
  backgroundColor: '#FFF',
  zIndex: 1,
});

export const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    display: 'flex',
    padding: '6px 12px',
    alignItems: 'center',
    borderRadius: '6px',
    border: '1px solid #DCE0E7',
    background: '#F8F9FC',
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '-0.14px',
    color: theme.palette.lightGray.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const CommonButton = styled(Button)({
  color: theme.palette.text.secondary,
  fontSize: '12px',
  fontWeight: 400,
  textTransform: 'none',
});

const TalkButton = styled(Button)({
  backgroundColor: '#5A4CE6',
  color: '#FFFFFF',
  boxShadow: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 400,
  width: 'fit-content',
  textTransform: 'none',

  '&:hover': {
    backgroundColor: '#4A3ED6',
    boxShadow: 'none',
  },

  '&.Mui-disabled': {
    backgroundColor: theme.palette.lightGray.main,
    color: '#A0A4B8',
  },
});
const AssistantInfo = () => {
  const queryClient = useQueryClient();
  const { selectedAssistant, setSelectedAssistant } = useDashboard();
  const { isLoading } = useAgents();
  const { data: templates } = useTemplates();
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(selectedAssistant?.agentName || '');
  const { alert, setAlert, showAlert } = useAlert();
  const navigate = useNavigate();
  const { mutateAsync: updateAssistant, isPending: isUpdateAgentPending } =
    useMutation({
      mutationFn: (values: IUpdateAssistant) => updateAgentApi(values),
      onSuccess: () => {
        if (selectedAssistant) {
          setSelectedAssistant({
            ...selectedAssistant,
            agentName: newName,
          });
        }
        const formattedName = newName.toLowerCase().replace(/\s+/g, '-');
        navigate(`/agent/${encodeURIComponent(formattedName)}`, {
          replace: true,
        });
        queryClient.invalidateQueries({
          queryKey: ['agents'],
        });
        showAlert('Agent name updated', 'success');
      },
      onError: (error: any) => {
        showAlert(
          error.response?.data?.message ||
            'Failed to update assistant system prompt',
          'error'
        );
      },
    });

  useEffect(() => {
    setNewName(selectedAssistant?.agentName || '');
  }, [selectedAssistant]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Skeleton variant="text" width={200} height={36} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width={140} height={20} />
        </Box>
      </Box>
    );
  }

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    updateAssistant({ agentId: selectedAssistant?._id || '', name: newName });
    setIsEditing(false);
  };

  // Function to get agent type name
  const getAgentTypeName = () => {
    if (!selectedAssistant?.templateId || !templates?.data?.templates) {
      return 'Unknown';
    }

    const template = templates.data.templates.find(
      (t: any) => t._id === selectedAssistant.templateId
    );

    if (!template) return 'Unknown';

    switch (template.name) {
      case 'Food Ordering':
        return 'Food Ordering';
      case 'General Queries':
        return 'General FAQ';
      case 'Automate Reservation':
        return 'Reservation';
      default:
        return template.name;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <ArrowLeftIcon
        size={20}
        onClick={() => navigate(routes.agents)}
        style={{ marginTop: '8px', cursor: 'pointer' }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditing ? (
            <TextField
              value={newName}
              onChange={e => setNewName(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
          ) : (
            <Typography
              variant="h6"
              color="custom.textBlue"
              sx={{ fontSize: '24px', fontWeight: 600 }}
            >
              {selectedAssistant?.agentName}
            </Typography>
          )}
          {!isEditing && (
            <IconButton size="small" onClick={handleEditClick}>
              <PencilSimpleIcon fontSize="small" />
            </IconButton>
          )}

          {isEditing && (
            <Button variant="text" size="small" onClick={handleSaveClick}>
              {isUpdateAgentPending ? 'Updating...' : 'Save'}
            </Button>
          )}
        </Box>

        {/* Agent Type Display */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            fontWeight={500}
            fontSize={12}
            color="custom.textBlue"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {getAgentTypeName()}
          </Typography>
        </Box>

        <MessagePopup alert={alert} setAlert={setAlert} />
      </Box>
    </Box>
  );
};

const Controls = () => {
  const { selectedAssistant } = useDashboard();
  const { isLoading, agents } = useAgents();
  const { user } = useUser();
  const [mode, setMode] = useState<string>('web');
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Change from single store to multiple stores
  const [selectedStores, setSelectedStores] = useState<any[]>([]);
  const [knowledgeBaseAnchorEl, setKnowledgeBaseAnchorEl] =
    useState<null | HTMLElement>(null);
  const { storesList, storesListLoading } = useStores();

  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: templates } = useTemplates();
  const { copy } = useCopyToClipboard();

  // Add state to track which specific item was copied
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  // Get call state from Redux
  const callState = useSelector((state: RootState) => state.call);
  const {
    isCallModalOpen,
    isConnected,
    ultravoxStatus,
    agentId: activeCallAgentId,
  } = callState;

  const { mutateAsync: deleteAgent, isPending: isDeletePending } = useMutation({
    mutationFn: deleteAgentApi,

    onSuccess: () => {
      showAlert('Agent deleted successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      setIsDeleteModalOpen(false);
      navigate(routes.agents);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to delete agent',
        'error'
      );
    },
  });

  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false);
  const [isConnectWhatsappModalOpen, setIsConnectWhatsappModalOpen] =
    useState(false);

  const handleModeChange = (event: SelectChangeEvent<unknown>) => {
    const selectedMode = event.target.value as string;
    setMode(selectedMode);

    if (selectedMode === 'phone') {
      setIsPhoneModalOpen(true);
    }
  };

  const handlePhoneModalClose = () => {
    setIsPhoneModalOpen(false);
    setMode('web');
  };

  const handleTalkClick = () => {
    // Only allow starting a new call if no call is active
    if (!isCallModalOpen && !isConnected) {
      dispatch(
        startCall({
          agentId: selectedAssistant?._id || '',
          agentKey: selectedAssistant?.agentKey || '',
          agentName: selectedAssistant?.agentName || '',
        })
      );
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    deleteAgent(selectedAssistant?._id || '');
  };

  const handleViewCallLogs = () => {
    localStorage.setItem('selectedAgent', JSON.stringify(selectedAssistant));
    navigate(routes.calls);
  };

  // Update the function to get all selected knowledge base stores
  const getSelectedKnowledgeBaseStores = useCallback(() => {
    if (!selectedAssistant?.config?.knowledgeBase?.length) return [];

    return selectedAssistant.config.knowledgeBase
      .map(kb => kb.store)
      .filter(Boolean);
  }, [selectedAssistant]);

  // Set initial selected stores from knowledgeBase if exists
  useEffect(() => {
    const knowledgeBaseStores = getSelectedKnowledgeBaseStores();
    if (knowledgeBaseStores.length > 0) {
      const completeStores = knowledgeBaseStores
        .map(kbStore => {
          const completeStore = storesList.find(
            (store: any) => String(store.id) === String(kbStore.id)
          );
          return completeStore || kbStore;
        })
        .filter(store => store && store.id && store.friendly_name);

      setSelectedStores(prev => {
        const prevIds = prev
          .map(s => String(s.id))
          .sort()
          .join(',');
        const newIds = completeStores
          .map(s => String(s.id))
          .sort()
          .join(',');
        return prevIds === newIds ? prev : completeStores;
      });
    } else {
      setSelectedStores(prev => (prev.length === 0 ? prev : []));
    }
  }, [selectedAssistant, getSelectedKnowledgeBaseStores, storesList]);

  const handleStoreSelect = (store: any) => {
    setSelectedStores(prev => {
      const isAlreadySelected = prev.some(s => s.id === store.id);
      if (isAlreadySelected) {
        // Remove store if already selected
        return prev.filter(s => s.id !== store.id);
      } else {
        // Add store if not selected - use the complete store data from storesList
        return [...prev, store];
      }
    });
  };

  const { mutateAsync: saveStore, isPending: isSaveStorePending } = useMutation(
    {
      mutationFn: (storeData: any) =>
        saveAgentKnowledgeBaseApi(storeData, user.tenantName || ''),
      onSuccess: () => {
        showAlert('Knowledge base updated successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        setKnowledgeBaseAnchorEl(null);
      },
      onError: (error: any) => {
        showAlert(
          error?.response?.data?.message || 'Failed to update knowledge base',
          'error'
        );
      },
    }
  );

  const handleConfirmSelection = async () => {
    if (!selectedStores.length || !selectedAssistant?._id) return;

    // Filter out any incomplete store objects before sending
    const validStores = selectedStores.filter(
      store => store && store.id && store.friendly_name && store.street_address // Ensure we have at least basic required fields
    );

    if (validStores.length === 0) {
      showAlert('No valid stores selected', 'error');
      return;
    }

    // Create array of store data for multiple stores
    const storesData = validStores.map(selectedStore => ({
      friendly_name: selectedStore.friendly_name,
      id: selectedStore.id,
      street_address: selectedStore.street_address,
      zip_code: selectedStore.zip_code,
      city: selectedStore.city,
      country: selectedStore.country,
      state_province: selectedStore.state_province,
      poc_name: selectedStore.poc_name,
      poc_email: selectedStore.poc_email,
      poc_phone: selectedStore.poc_phone,
      poc_role: selectedStore.poc_role,
      mode_of_operation: selectedStore.mode_of_operation,
      brand_ids: selectedStore.brand_ids,
      opening_hours: selectedStore.opening_hours || [],
      storeknowledgeBase: selectedStore.storeknowledgeBase,
      reservation: selectedStore.reservation,
    }));

    const storeData = {
      agentId: selectedAssistant._id,
      storeData: storesData,
    };

    await saveStore(storeData);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Skeleton variant="rectangular" width={200} height={36} />
        <Skeleton variant="rectangular" width={150} height={36} />
        <Skeleton variant="rectangular" width={180} height={36} />
      </Box>
    );
  }

  // Get the current knowledge base stores for display
  const currentKnowledgeBaseStores = getSelectedKnowledgeBaseStores();
  const currentStoreNames =
    currentKnowledgeBaseStores.length > 0
      ? currentKnowledgeBaseStores.map(store => store.friendly_name).join(', ')
      : 'Select Stores';

  // Check if we're viewing the agent we're currently on a call with
  const isCurrentAgentInCall = selectedAssistant?._id === activeCallAgentId;
  const isCallActive = isCallModalOpen || isConnected;

  const getButtonText = () => {
    // Show detailed status only if we're viewing the agent we're talking to
    if (isCallActive && isCurrentAgentInCall) {
      switch (ultravoxStatus) {
        case 'connecting':
          return 'Connecting...';
        case 'speaking':
          return 'Agent Speaking';
        case 'listening':
          return 'You Can Speak';
        case 'thinking':
          return 'Agent Thinking...';
        default:
          return 'Call Active';
      }
    }
    return 'Talk to Agent';
  };

  const getButtonColor = () => {
    if (isCallActive && isCurrentAgentInCall) {
      // Show dynamic colors for the agent we're talking to
      switch (ultravoxStatus) {
        case 'speaking':
          return '#2ECC71'; // Green when agent is speaking
        case 'listening':
          return '#3498DB'; // Blue when user can speak
        case 'thinking':
          return '#F39C12'; // Orange when thinking
        default:
          return '#95A5A6'; // Gray for other states
      }
    }
    return isCallActive ? '#95A5A6' : '#5A4CE6'; // Gray if call active but different agent, purple if no call
  };

  const handleCopyClick = (type: string) => {
    if (type === 'agentKey') {
      copy(selectedAssistant?.agentKey || '');
      setCopiedItem('agentKey');
    } else if (type === 'agentId') {
      copy(selectedAssistant?._id || '');
      setCopiedItem('agentId');
    }

    // Reset the copied item state after the same timeout as the hook
    setTimeout(() => {
      setCopiedItem(null);
    }, 3000);
  };

  // Function to check if the current agent is a Food Ordering agent
  const isFoodOrderingAgent = () => {
    if (!selectedAssistant?.templateId || !templates?.data?.templates) {
      return false;
    }

    const template = templates.data.templates.find(
      (t: any) => t._id === selectedAssistant.templateId
    );

    return template?.name === 'Food Ordering';
  };

  const handleAgentMenuClick = () => {
    // Close menu immediately without waiting
    setAnchorEl(null);
    // Use setTimeout to ensure menu closes before opening drawer
    setTimeout(() => {
      setIsMenuDrawerOpen(true);
    }, 0);
  };

  const handleMenuDrawerClose = () => {
    setIsMenuDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CommonButton>Stores</CommonButton>{' '}
          {/* Changed from "Store" to "Stores" */}
          <Button
            size="small"
            onClick={e => setKnowledgeBaseAnchorEl(e.currentTarget)}
            sx={{
              width: 'fit-content',
              height: 36,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'lightGray.main',
              backgroundColor: '#F8F9FC',
              padding: '6px 12px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 400,
              color: 'custom.textBlue',
            }}
            endIcon={
              <IconButton size="small" sx={{ p: 0, m: 0 }}>
                <CaretDownIcon size={15} />
              </IconButton>
            }
          >
            <Typography
              sx={{
                maxWidth: '180px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {currentStoreNames}
            </Typography>
          </Button>
        </Box>

        <Menu
          anchorEl={knowledgeBaseAnchorEl}
          open={Boolean(knowledgeBaseAnchorEl)}
          onClose={() => setKnowledgeBaseAnchorEl(null)}
          keepMounted
          disableAutoFocusItem
          slotProps={{
            paper: {
              sx: {
                width: '230px',
                mt: 1,
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                border: '1px solid #E0E0E0',
              },
            },
          }}
        >
          {storesListLoading ? (
            <MenuItem disabled>
              <Typography sx={{ fontSize: '14px' }}>
                Loading stores...
              </Typography>
            </MenuItem>
          ) : storesList.length === 0 ? (
            <MenuItem disabled>
              <Typography sx={{ fontSize: '14px' }}>No stores found</Typography>
            </MenuItem>
          ) : (
            storesList.map((store: any) => {
              const isCurrentlySelected = selectedStores.some(
                selectedStore => String(selectedStore.id) === String(store.id)
              );

              return (
                <MenuItem
                  key={store.id}
                  dense
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleStoreSelect(store);
                  }}
                  sx={{
                    padding: '0px 0px',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isCurrentlySelected}
                        size="small"
                        sx={{
                          '&.Mui-checked': {
                            color: '#5A4CE6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: '14px',
                          maxWidth: '160px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {store.friendly_name}
                      </Typography>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </MenuItem>
              );
            })
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 1,
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Button
              size="small"
              onClick={() => {
                setKnowledgeBaseAnchorEl(null);
                // Reset to current knowledge base stores with proper filtering
                const knowledgeBaseStores = getSelectedKnowledgeBaseStores();
                const completeStores = knowledgeBaseStores
                  .map(kbStore => {
                    const completeStore = storesList.find(
                      (store: any) => String(store.id) === String(kbStore.id)
                    );
                    return completeStore || kbStore;
                  })
                  .filter(store => store && store.id && store.friendly_name);
                setSelectedStores(completeStores);
              }}
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                color: 'text.secondary',
                mr: 1,
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!selectedStores.length || isSaveStorePending}
              onClick={handleConfirmSelection}
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                boxShadow: 'none',
                borderRadius: '6px',
                px: 2,
              }}
            >
              {isSaveStorePending ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Menu>

        <CommonButton>Mode</CommonButton>
        <StyledSelect
          value={mode}
          onChange={handleModeChange}
          sx={{
            borderRadius: '6px',
            border: '1px solid lightGray.main',
            backgroundColor: '#FFF',
            color: 'custom.textBlue',
            fontSize: '16px',
          }}
        >
          <MenuItem
            value="web"
            sx={{ fontSize: '14px', color: 'custom.textBlue' }}
          >
            Web
          </MenuItem>
          <MenuItem
            value="phone"
            sx={{ fontSize: '14px', color: 'custom.textBlue' }}
          >
            Phone
          </MenuItem>
        </StyledSelect>
      </Box>

      <Box
        sx={{
          display: 'flex',
          padding: '8px 16px',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {selectedAssistant?.agentChannel === 'whatsapp' ? (
          selectedAssistant?.config?.whatsappConfig?.linkedVoiceAgentId ? (
            (() => {
              const linkedVoiceAgentId =
                selectedAssistant.config.whatsappConfig.linkedVoiceAgentId;
              const voiceAgentName =
                agents?.agents?.find((a: any) => a._id === linkedVoiceAgentId)
                  ?.agentName ?? 'Voice Agent';
              return (
                <Tooltip
                  title={`Connected to ${voiceAgentName} (Voice Agent)`}
                  placement="bottom"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.5,
                      py: '4px',
                      height: 36,
                      borderRadius: '6px',
                      border: '1px solid #DCE0E7',
                      backgroundColor: '#FFFFFF',
                      cursor: 'default',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#25D366',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#25D366',
                        lineHeight: 1.2,
                      }}
                    >
                      Connected
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })()
          ) : null
        ) : (
          <TalkButton
            variant="contained"
            startIcon={<PhoneIcon size={16} />}
            onClick={handleTalkClick}
            disabled={isCallActive}
            sx={{
              backgroundColor: getButtonColor(),
              '&:hover': {
                backgroundColor: getButtonColor(),
              },
              '&.Mui-disabled': {
                backgroundColor: '#95A5A6',
                color: '#FFFFFF',
              },
            }}
          >
            <Typography
              sx={{
                textTransform: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getButtonText()}
            </Typography>
          </TalkButton>
        )}
      </Box>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={handleMenuOpen}>
          <DotsThreeVerticalIcon size={20} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: 1,
            width: '240px',
            border: '1px solid #E0E0E0',
          },
        }}
      >
        <MenuItem
          onClick={handleViewCallLogs}
          sx={{
            fontSize: '14px',
            color: 'custom.textBlue',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <NotebookIcon size={18} />
          Call Logs
        </MenuItem>
        <Divider sx={{ mx: 2 }} />
        <MenuItem
          onClick={() => handleCopyClick('agentKey')}
          sx={{
            color: 'custom.textBlue',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {copiedItem === 'agentKey' ? (
            <CheckIcon size={18} />
          ) : (
            <CopyIcon size={18} />
          )}
          Copy Agent Key
        </MenuItem>
        <Divider sx={{ mx: 2 }} />
        <MenuItem
          onClick={() => handleCopyClick('agentId')}
          sx={{
            color: 'custom.textBlue',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {copiedItem === 'agentId' ? (
            <CheckIcon size={18} />
          ) : (
            <CopyIcon size={18} />
          )}
          Copy Agent Id
        </MenuItem>
        <Divider sx={{ mx: 2 }} />
        {/* Conditionally render Agent Menu button for Food Ordering agents */}
        {isFoodOrderingAgent() && (
          <MenuItem
            onClick={handleAgentMenuClick}
            sx={{
              color: 'custom.textBlue',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ForkKnifeIcon size={18} />
            Agent Menu
          </MenuItem>
        )}
        {isFoodOrderingAgent() && <Divider sx={{ mx: 2 }} />}

        {selectedAssistant?.agentChannel !== 'whatsapp' && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setIsConnectWhatsappModalOpen(true);
            }}
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: selectedAssistant?.config?.linkedWhatsappAgent?.agentId
                ? 'error.main'
                : 'custom.textBlue',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {selectedAssistant?.config?.linkedWhatsappAgent?.agentId ? (
                <LinkBreakIcon size={18} />
              ) : (
                <WhatsappLogoIcon size={18} />
              )}
            </Box>
            {selectedAssistant?.config?.linkedWhatsappAgent?.agentId
              ? 'Disconnect WhatsApp'
              : 'Connect WhatsApp Agent'}
          </MenuItem>
        )}
        {selectedAssistant?.agentChannel !== 'whatsapp' && (
          <Divider sx={{ mx: 2 }} />
        )}

        <MenuItem
          onClick={handleDeleteClick}
          sx={{
            color: 'error.main',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <TrashSimpleIcon size={18} />
          Delete Agent
        </MenuItem>
      </Menu>

      <PhoneModal
        open={isPhoneModalOpen}
        onClose={handlePhoneModalClose}
        agentId={selectedAssistant?._id || ''}
        agentKey={selectedAssistant?.agentKey || ''}
        showAlert={showAlert}
      />

      <DeleteModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDelete}
        isPending={isDeletePending}
        title="Delete Agent"
        description="Are you sure you want to delete this agent? This action cannot be undone."
      />

      <MessagePopup alert={alert} setAlert={setAlert} />

      <ConnectWhatsappAgentModal
        open={isConnectWhatsappModalOpen}
        onClose={() => setIsConnectWhatsappModalOpen(false)}
        voiceAgentId={selectedAssistant?._id ?? ''}
        linkedWhatsappAgentId={
          selectedAssistant?.config?.linkedWhatsappAgent?.agentId ?? null
        }
        onSuccess={msg => showAlert(msg, 'success')}
      />

      <MenuDrawer open={isMenuDrawerOpen} onClose={handleMenuDrawerClose} />
    </Box>
  );
};

function AgentInfoHeader() {
  return (
    <HeaderContainer>
      <AssistantInfo />
      <Controls />
    </HeaderContainer>
  );
}

export default AgentInfoHeader;
